import React, { useCallback, useMemo } from 'react';
import cn from 'bem-cn';

import { EDITORIAL_UID, PromptStatus } from '@ai-platform/utils';
import noImage from '../../../images/no_image.png';
import prompthubEllipse from '../../../images/icons/prompthub-ellipse.svg';

import { RoutePaths } from '../../../constants';
import { Button, Link, Video } from '../../UI';
import { ScreenSize, useGetScreenSize, useLike, useVideoOnHover, MetrikaGoal, useMetrika } from '../../../hooks';
import { sanitize } from '../../../utils/sanitize';
import { openAliceChat } from '../../../utils/alice/alice';

import { useParallax } from '../useParallax';
import { Like } from '../components/Like';
import { Status } from '../components/Status';
import { PromptCardProps } from '../PromptCard.types';
import { usePromptCardVisibility } from '../../../VisibilityTracker';
import './PromptCard2.scss';

const b = cn('prompt-card-alter');

export const PromptCard: React.FC<PromptCardProps> = ({
    prompt,
    size = 'normal',
    className,
    withHover,
    showStatus = false,
    needMarkAsViewed,
}) => {
    const screenSize = useGetScreenSize();

    const {
        title,
        description,
        videoData: { url: videoUrl },
        imageData: { urlOptimized: imageUrl },
        categories,
    } = prompt;
    const sanitaziedTitle = sanitize(title);
    const sanitaziedDescription = sanitize(description);
    const authorImage = prompt.author?.image || prompthubEllipse;

    const isPhotoRevival = categories?.includes('photo-revival');
    const isFullImageCard = prompt.author?.uid === EDITORIAL_UID || isPhotoRevival;
    const needHover = withHover && screenSize !== ScreenSize.Mobile && screenSize !== ScreenSize.Tablet;

    const mainColor: string = prompt.imageData?.colors?.MainColor;

    const { likes, isLiked, toggleLike } = useLike({
        promptId: Number(prompt.id),
        initialLikes: prompt.likes || 0,
        initialIsLiked: prompt.isLiked || false,
    });

    const { panelRef, transformX, transformY } = useParallax(1);

    usePromptCardVisibility(panelRef, { prompt, needMarkAsViewed });

    // Хук для управления видео при ховере (только для photo-revival карточек)
    const { videoRef, onMouseEnter: onVideoMouseEnter, onMouseLeave: onVideoMouseLeave } = useVideoOnHover();
    const { reachGoal } = useMetrika();

    const handleOpenAliceChat = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            openAliceChat(prompt?.id);
            reachGoal(MetrikaGoal.ClickoutChat);
        },
        [prompt, reachGoal],
    );

    const parallaxStyle = {
        transform: `perspective(200px) rotateY(${transformX}deg) rotateX(${transformY}deg)`,
    };

    // Для editorial карточек масштабирование применяется к background
    // Для обычных карточек - ко всей карточке
    const cardParallaxStyle = needHover ? parallaxStyle : {};

    const bgImage = isFullImageCard ? `url(${imageUrl})` : '';
    const backgroundStyle = {
        '--bg-image': bgImage,
        '--image-main-color': isFullImageCard ? mainColor : '',
    } as React.CSSProperties;

    const promptLink = useMemo(() => {
        if (prompt.status === PromptStatus.Published) {
            return `${RoutePaths.Prompt}/${prompt.id}`;
        }

        return `${RoutePaths.NewPromptStatus}/${prompt.id}`;
    }, [prompt.id, prompt.status]);

    const renderProfile = () => {
        if (authorImage) {
            return <img src={authorImage} alt={'Аватарка'} className={b('avatar')} />;
        }

        return <div />;
    };

    return (
        <Link
            to={promptLink}
            state={{ fromClient: true }}
            ref={panelRef}
            className={b(
                { size, 'full-image': isFullImageCard, 'with-hover': needHover, 'photo-revival': isPhotoRevival },
                [className],
            )}
            onMouseEnter={isPhotoRevival ? onVideoMouseEnter : undefined}
            onMouseLeave={isPhotoRevival ? onVideoMouseLeave : undefined}
        >
            {needHover && <div className={b('hover-trigger')} />}
            <div style={cardParallaxStyle} className={b('parallax')}>
                <div className={b('background')} style={backgroundStyle}>
                    {isPhotoRevival && Boolean(videoUrl) && (
                        <Video
                            ref={videoRef}
                            className={b('video')}
                            src={videoUrl}
                            muted
                            loop
                            playsInline
                            preload="metadata"
                        />
                    )}
                </div>
                <div className={b('content')}>
                    <div className={b('header')}>
                        {showStatus && prompt.status ? <Status status={prompt.status} /> : renderProfile()}
                        {(!showStatus || prompt.status === PromptStatus.Published) && (
                            <Like
                                size={size}
                                count={likes}
                                isEditorial={isFullImageCard}
                                isLiked={isLiked}
                                onClick={toggleLike}
                            />
                        )}
                    </div>
                    <div className={b('body')}>
                        <div className={b('front-side')}>
                            {!isFullImageCard && (
                                <div className={b('image-block')}>
                                    <img src={imageUrl || noImage} alt={prompt.title} className={b('image')} />
                                </div>
                            )}
                            <div className={b('title')}>
                                <p dangerouslySetInnerHTML={{ __html: sanitaziedTitle }} />
                            </div>
                        </div>
                        <div className={b('back-side')}>
                            <div className={b('alter-text')}>
                                <p dangerouslySetInnerHTML={{ __html: sanitaziedDescription }} />
                            </div>
                        </div>
                    </div>
                    <div className={b('button-group')}>
                        <Button
                            variant="primary"
                            fullWidth={!isPhotoRevival}
                            className={b('alter-button')}
                            onClick={handleOpenAliceChat}
                        >
                            Попробовать в Алисе AI
                        </Button>
                    </div>
                </div>
            </div>
        </Link>
    );
};
